import { useEffect, useState } from "react";
import "../styles/competitions.css";

import heroBg    from "../photos/competitions.jpg";
import hackImg   from "../photos/competitions00.png";
import scrabImg  from "../photos/competitions01.png";
import quizImg   from "../photos/competitions02.png";
import sudokuImg from "../photos/competitions03.png";
import mtapImg   from "../photos/competitions04.jpg";

const GROUPS = [
  {
    id: "quizbee",
    label: "Quiz Bee",
    color: "cyan",
    photo: quizImg,
    quote: "Every question was a checkpoint — not just for knowledge, but for composure under pressure.",
    entries: [
      { year: "2023", event: "E-Cup 2023–2024", role: "1st Year Quizzer",  result: "1st Runner Up" },
      { year: "2025", event: "E-Cup 2024–2025", role: "2nd Year Quizzer",  result: "3rd Runner Up" },
      { year: "2026", event: "E-Cup 2025–2026", role: "3rd Year Quizzer",  result: "1st Runner Up" },
    ],
  },
  {
    id: "hackathon",
    label: "Hackathon",
    color: "teal",
    photo: hackImg,
    quote: "Hours of coding compressed into minutes of output — the hackathon taught me what I'm made of when the clock counts down.",
    entries: [
      { year: "2025", event: "C Programming Hackathon", role: "C Programming Participant", result: "Certificate of Participation · 4th Runner Up" },
    ],
  },
  {
    id: "mtap",
    label: "MTAP Mathematics",
    color: "purple",
    photo: mtapImg,
    quote: "Mathematics was my first competition arena — where I learned that numbers don't lie, and neither does hard work.",
    entries: [
      { year: "2017 (G7)", event: "District Level",  role: "School Participant",   result: "Champion & Most Promising" },
      { year: "2017 (G7)", event: "Division Level",  role: "District Participant", result: "Certificate of Participation" },
      { year: "2018 (G8)", event: "District Level",  role: "School Participant",   result: "1st Runner Up & Most Promising" },
      { year: "2018 (G8)", event: "Division Level",  role: "District Participant", result: "Certificate of Participation" },
    ],
  },
  {
    id: "sudoku",
    label: "Sudoku",
    color: "gold",
    photo: sudokuImg,
    quote: "Logic, patience, and speed — Sudoku is a quiet battle that trains the mind for any challenge.",
    entries: [
      { year: "2023", event: "E-Cup 2023–2024",         role: "CpE Contestant",         result: "2nd Runner Up" },
      { year: "2025", event: "E-Cup 2024–2025",         role: "CpE Contestant",         result: "Champion 🏆" },
      { year: "2024", event: "BravEvolution 2024–2025", role: "Engineering Contestant", result: "1st Runner Up" },
      { year: "2026", event: "E-Cup 2025–2026",         role: "CpE Contestant",         result: "Champion 🏆" },
      { year: "2026", event: "BravEvolution 2025–2026", role: "Engineering Contestant", result: "3rd Runner Up" },
    ],
  },
  {
    id: "scrabble",
    label: "Scrabble",
    color: "coral",
    photo: scrabImg,
    quote: "Words on a board became a testament — language and strategy are engineering disciplines of their own.",
    entries: [
      { year: "2024", event: "BravEvolution 2023–2024", role: "Engineering Reserve", result: "Champion 🏆" },
    ],
  },
];

function useReveal() {
  useEffect(() => {
    const attach = () => {
      const nodes = document.querySelectorAll(".cmp-reveal:not(.is-visible)");
      if (!nodes.length) return;
      const obs = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); }
          }),
        { root: null, threshold: 0.04, rootMargin: "0px 0px -10px 0px" }
      );
      nodes.forEach((n) => obs.observe(n));
      return obs;
    };

    let obs = attach();
    const mut = new MutationObserver(() => { obs?.disconnect(); obs = attach(); });
    mut.observe(document.body, { childList: true, subtree: true });
    return () => { obs?.disconnect(); mut.disconnect(); };
  }, []);
}

function StatsStrip() {
  const stats = [
    { val: "14", lbl: "Total Competitions" },
    { val: "5",  lbl: "Champions"          },
    { val: "5",  lbl: "Runner Ups"         },
    { val: "5+", lbl: "Years Active"       },
  ];
  return (
    <div className="cmp-stats cmp-reveal">
      {stats.map((s) => (
        <div key={s.lbl} className="cmp-stat">
          <span className="cmp-stat__val">{s.val}</span>
          <span className="cmp-stat__lbl">{s.lbl}</span>
        </div>
      ))}
    </div>
  );
}

function CompGroup({ group, index }) {
  const [open, setOpen] = useState(false);

  const colorMap = {
    cyan:   "#44f9ff",
    teal:   "#3cf0c0",
    purple: "#9b72f0",
    gold:   "#f0a84e",
    coral:  "#e86c6c",
  };
  const accent = colorMap[group.color] || "#44f9ff";

  return (
    <div
      className={`cmp-group cmp-reveal cmp-group--${group.color}${open ? " cmp-group--open" : ""}`}
      style={{ transitionDelay: `${index * 0.07}s`, "--accent": accent }}
    >
      {/* Trigger — no icon */}
      <button
        className="cmp-group__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="cmp-group__label">{group.label}</span>
        <span className="cmp-group__count">
          {group.entries.length} event{group.entries.length !== 1 ? "s" : ""}
        </span>
        <span className={`cmp-group__arrow${open ? " cmp-group__arrow--open" : ""}`}>▼</span>
      </button>

      {/* Expanded panel */}
      <div className={`cmp-group__entries${open ? " cmp-group__entries--open" : ""}`}>
        <div className="cmp-group__entries-inner">

          {/* Quote block — centered */}
          <div className="cmp-group__quote">
            <span className="cmp-group__quote-mark">"</span>
            <p className="cmp-group__quote-text">{group.quote}</p>
          </div>

          {/* Entry rows */}
          {group.entries.map((entry, i) => (
            <div key={i} className="cmp-entry">
              {/* Left photo */}
              <div className="cmp-entry__photo-wrap">
                <img src={group.photo} alt={group.label} className="cmp-entry__photo" />
                <div className="cmp-entry__photo-overlay" />
                <span className="cmp-entry__year">{entry.year}</span>
              </div>
              {/* Right content — centered */}
              <div className="cmp-entry__content">
                <p className="cmp-entry__event">{entry.event}</p>
                <p className="cmp-entry__role">{entry.role}</p>
                <div className={`cmp-entry__result cmp-entry__result--${group.color}`}>
                  {entry.result}
                </div>
              </div>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
}

export default function Competitions() {
  useReveal();

  return (
    <div className="cmp-page">

      {/* Hero */}
      <div className="cmp-hero cmp-reveal">
        <img src={heroBg} alt="" className="cmp-hero__bg" aria-hidden="true" />
        <div className="cmp-hero__overlay" />
        <div className="cmp-hero__content">
          <h1 className="cmp-hero__title">Competing · Growing · Winning</h1>
          <p className="cmp-hero__sub">
            From Grade 7 mathematics podiums to college hackathons and quiz bees —<br />
            every arena sharpened a different edge.
          </p>
        </div>
      </div>

      {/* Thin horizontal rule between hero and stats */}
      <div className="cmp-hero-rule cmp-reveal" />

      {/* Stats */}
      <StatsStrip />

      {/* Thin horizontal rule below stats */}
      <div className="cmp-hero-rule cmp-reveal" />

      {/* Section label */}
      <p className="cmp-section-label cmp-reveal">— Click any category to expand —</p>

      {/* Accordion */}
      <div className="cmp-groups">
        {GROUPS.map((g, i) => (
          <CompGroup key={g.id} group={g} index={i} />
        ))}
      </div>

    </div>
  );
}